import { useEffect, useState } from 'preact/hooks';
import {
  getMyRecord,
  listAllRecordsByType,
  transitionRecord,
  type RecordRow,
} from '../lib/api';
import { routePath } from '../routes/route';
import { useRecordsVersion } from '../lib/recordEditor';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { FieldStatus } from '../components/FieldStatus';
import { SourceThumb } from '../components/SourceThumb';
import {
  EQUIPMENT_TYPE,
  RATE_TYPE,
  VISIT_CLOSED,
  clockTime,
  currentJob,
  isSameDay,
  jobTitle,
  jobsFrom,
  jobsToday,
  linkedId,
  linkedIds,
  readFieldDay,
  shortDate,
  type FieldJob,
  type FieldRead,
} from '../lib/fieldDay';
import './Job.css';

// S5a — THE JOB (wf03 §01, blessed 2026-08-06).
//
// "GETTING IN" IS AT THE TOP, ABOVE EVERYTHING. wf03 puts it there and the
// spec restates why: *"Rear loading dock, buzz 3. Not over lunch. Ask for
// Marta."* is `hvac_site.access_notes`, and it is first because it is the
// question the tech actually has, standing outside the building at 9am.
// Everything else on this screen can be scrolled to; this cannot.
//
// Then, in wf03's order: On site (the primary action, full width) ·
// Photo/attach · Issue · Equipment · Last visit · the flat rate and book
// hours. ONE primary action, at full width, because gloves assume big
// targets.
//
// ---------------------------------------------------------------------
// WHAT THE ROUTE CARRIES, AND WHY IT IS THE WORK ORDER
//
// `/jobs/detail/:id` names the WORK ORDER, not the visit. Two reasons: the
// job is what wf03's header shows (WO-4417), and the Knot's pack-declared
// capture contexts are hvac_work_order / hvac_client / hvac_site — so a
// route carrying the work-order id is the one that lets a capture taken on
// this screen pre-associate with the job instead of falling back to a bare
// note (App.tsx's CAPTURE_SUBJECT_BY_SCREEN).
//
// The bare `jobs` TAB carries no id and means "the job I am on now" — see
// lib/fieldDay's `currentJob`. Both cases run the SAME read, which is what
// makes them cost the same.
//
// ---------------------------------------------------------------------
// WHAT IT READS (all batched — lib/fieldDay's four sweeps, plus three)
//
//   the four sweeps            the day, its jobs, their sites and clients
//   hvac_equipment             names for the ids the work order links to
//   hvac_rate                  the flat-rate book
//   GET /api/me/record         who I am, so "· you" is a fact
//
// Nothing here is per row. The budget is asserted in requestBudget.test.tsx.
//
// ---------------------------------------------------------------------
// THE FLAT RATE — THE ONE LINE wf03 DRAWS THAT THE DATA MODEL CANNOT JOIN
//
// `hvac_rate` is STANDALONE in this pack: no work order, visit or material
// line carries a relationship to one (recordTypes.ts's own seed-order note
// says so — "6. hvac_rate — standalone"). So there is no link to follow.
// What there IS is `hvac_work_order.priority`, kept as free TEXT precisely
// because it is where the flat-rate H-code taxonomy will land once Alan
// enumerates it (recordTypes.ts's note on that field). This screen matches
// that text against `hvac_rate.rate_type` and shows the book hours when the
// book has that rate — and says plainly when the job names no rate, or
// names one the book does not carry. Nothing is derived and nothing is
// invented; where the model cannot answer, the line says so.

export interface JobProps {
  tenantId: string;
  /** The work order. Absent on the bare `jobs` tab. */
  id?: string;
  onNavigate: (path: string) => void;
  /** Opens the Knot's capture sheet — the instrument that actually takes a
   *  photo, a file or a voice note (components/CaptureSheet). wf03's
   *  "Photo · attach" reaches it rather than growing a second one. */
  onCapture: () => void;
  /** CV3 §1 — the bare tab resolved WHICH job it is showing. The shell uses
   *  this to normalize the route to the canonical `/jobs/detail/:id` (a
   *  history REPLACE), which is what lets a capture taken from the Jobs TAB
   *  carry the work order in its envelope. Only ever called when `id` is
   *  absent; a day with no current job calls nothing. */
  onResolvedWorkOrder?: (workOrderId: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

/** The walk from wherever the visit is to `on_site`, one edge per step —
 *  taken straight from the pack's own hvac_visit lifecycle. `en_route` is a
 *  waypoint on that graph, not a claim this screen invents: the tech's act
 *  is "I am on site", and the edges are how the graph spells it. */
const TOWARD_ON_SITE: Record<string, string> = {
  proposed: 'scheduled',
  scheduled: 'en_route',
  en_route: 'on_site',
};

function text(r: RecordRow | null | undefined, name: string): string {
  if (!r) return '';
  const v = r.fields[name];
  return typeof v === 'string' ? v.trim() : '';
}

function num(r: RecordRow, name: string): number | null {
  const v = Number(r.fields[name]);
  return Number.isFinite(v) ? v : null;
}

/** A past visit at this location: when, and whether it was mine. */
interface LastVisit {
  on: Date;
  mine: boolean;
}

export function Job({ tenantId, id, onNavigate, onCapture, onResolvedWorkOrder }: JobProps) {
  const [load, setLoad] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [read, setRead] = useState<FieldRead | null>(null);
  const [jobs, setJobs] = useState<FieldJob[]>([]);
  const [equipment, setEquipment] = useState<Map<string, RecordRow>>(new Map());
  const [rates, setRates] = useState<RecordRow[]>([]);
  const [myRecordId, setMyRecordId] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [actionFailure, setActionFailure] = useState<ReadFailure | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;
    async function load_() {
      setLoad('loading');
      setFailure(null);
      setActionFailure(null);
      try {
        const [day, kit, book, mine] = await Promise.all([
          // `landed: true` — this is the one screen that renders "On this
          // job", so it is the one screen that pays the two landing sweeps.
          readFieldDay(tenantId, { landed: true }),
          listAllRecordsByType(tenantId, EQUIPMENT_TYPE),
          listAllRecordsByType(tenantId, RATE_TYPE),
          // Not knowing who I am costs the "· you" on a past visit and
          // nothing else — never the screen. (The route 404s on a build
          // whose pack binds no party record.)
          getMyRecord(tenantId).catch(() => null),
        ]);
        if (cancelled) return;
        setRead(day);
        setJobs(jobsFrom(day));
        setEquipment(new Map(kit.map((r) => [r.id, r])));
        setRates(book);
        setMyRecordId(mine?.id ?? null);
        setLoad('ready');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLoad('error');
      }
    }
    void load_();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, recordsVersion, retryNonce]);

  // CV3 §1 — the bare tab tells the shell which job it resolved, so the
  // route can become the canonical `/jobs/detail/:id`. Same resolution the
  // render below makes (currentJob over today's rows), run when the read is
  // in hand; a day with no current job stays on the bare tab and says so.
  useEffect(() => {
    if (load !== 'ready' || id !== undefined || !onResolvedWorkOrder) return;
    const now = new Date();
    const resolved = currentJob(jobsToday(jobs, now), now);
    if (resolved?.workOrder) onResolvedWorkOrder(resolved.workOrder.id);
    // jobs is the state the read produced; the callback is the shell's
    // per-render closure — deliberately not a dependency (the resolution
    // depends only on the day).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, id, jobs]);

  if (load === 'loading') {
    return (
      <div class="job">
        <FieldStatus />
        <div class="job__say">One moment…</div>
      </div>
    );
  }

  if (load === 'error' && failure !== null) {
    return (
      <div class="job">
        <FieldStatus />
        <div class="job__say">
          {failure.headline}
          <span class="job__say-soft">{failure.detail}</span>
          {failure.retryable ? (
            <button type="button" class="job__ghost" onClick={() => setRetryNonce((n) => n + 1)}>
              Read it again
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  const now = new Date();
  const job = id === undefined ? currentJob(jobsToday(jobs, now), now) : visitOn(jobs, id, now);

  if (job === null) {
    // TWO DIFFERENT ABSENCES, TWO DIFFERENT SENTENCES. A named job that is
    // not in the day is row scope answering correctly ("not yours"); no job
    // at all on the bare tab is simply the state of the day.
    return (
      <div class="job">
        <FieldStatus />
        <div class="job__say">
          {id === undefined ? 'No job open right now.' : "That job isn't on your day."}
          <span class="job__say-soft">
            {id === undefined
              ? 'When you start one it shows here. Your day is on Today.'
              : 'Only the jobs booked on your name reach this screen. Ask the office if you are standing in front of this one.'}
          </span>
          <button
            type="button"
            class="job__ghost"
            onClick={() => onNavigate(routePath({ screen: 'today' }))}
          >
            Back to today
          </button>
        </div>
      </div>
    );
  }

  const wo = job.workOrder;
  const site = job.site;
  const access = text(site, 'access_notes');
  const address = text(site, 'service_address');
  const visitState = job.visit.lifecycle_state ?? '';
  const nextState = TOWARD_ON_SITE[visitState];

  // ---- equipment: the ids the work order links to, named where we could
  // read them, counted where we could not.
  const woLinks = wo === null ? undefined : read?.woLinks[wo.id];
  const equipmentIds = wo && woLinks ? linkedIds(woLinks, wo.id, 'equipment') : [];
  const equipmentNames = equipmentIds
    .map((eid) => equipment.get(eid)?.display_name)
    .filter((n): n is string => typeof n === 'string');
  const equipmentUnread = equipmentIds.length - equipmentNames.length;

  // ---- last visit HERE (the site's own history), excluding this one.
  const lastVisit = latestOtherVisitHere(jobs, job, read, myRecordId);

  // ---- CV3: what LANDED on this job — the notes and retained captures
  // whose one link names this work order (the sweeps carry the links; the
  // filter is the same linkedId walk the rest of the screen rides). Newest
  // first, because "capture -> approve -> SEE IT" is this list's whole job.
  const landedNotes =
    wo === null || read === null
      ? []
      : read.notes.filter((n) => {
          const links = read.noteLinks[n.id];
          return links !== undefined && linkedId(links, n.id, 'note_work_order') === wo.id;
        });
  const landedSourcesOnJob =
    wo === null || read === null
      ? []
      : read.sources.filter((s) => {
          const links = read.sourceLinks[s.id];
          return links !== undefined && linkedId(links, s.id, 'source_work_order') === wo.id;
        });
  // ---- CV5 §4/§5 — WHICH NOTE EACH RECORDING BELONGS TO. A spoken note is
  // ONE utterance that became two rows: the transcript (hvac_note) and the
  // retained audio (hvac_source), joined by `source_note_record`. The list
  // shows the utterance ONCE — the note, which is the readable half — and the
  // note carries the way to HEAR it. Listing both would print the same
  // sentence twice and hide which of the two is the truth.
  const recordingForNote = new Map<string, RecordRow>();
  if (read !== null) {
    for (const s of landedSourcesOnJob) {
      const links = read.sourceLinks[s.id];
      const noteId = links === undefined ? null : linkedId(links, s.id, 'source_note_record');
      if (noteId !== null) recordingForNote.set(noteId, s);
    }
  }
  const recordingIds = new Set([...recordingForNote.values()].map((r) => r.id));
  const landedSources = landedSourcesOnJob.filter((s) => !recordingIds.has(s.id));
  const landed = [
    ...landedNotes.map((r) => ({ kind: 'note' as const, row: r })),
    ...landedSources.map((r) => ({ kind: 'source' as const, row: r })),
  ].sort((a, b) => (a.row.created_at < b.row.created_at ? 1 : -1));

  // ---- the flat rate. See this file's header on why it is a text match.
  const priority = text(wo, 'priority');
  const rate = priority
    ? (rates.find((r) => text(r, 'rate_type').toLowerCase() === priority.toLowerCase()) ?? null)
    : null;
  const rateHours = rate === null ? null : num(rate, 'hours');

  async function goOnSite() {
    if (acting) return;
    setActing(true);
    setActionFailure(null);
    try {
      let state = visitState;
      // The graph may need a waypoint (scheduled -> en_route -> on_site);
      // each edge is its own write, and the first refusal stops the walk and
      // SAYS so. A silent half-walk is exactly the failure this repo bans.
      for (let step = 0; step < 3 && state !== 'on_site'; step++) {
        const to = TOWARD_ON_SITE[state];
        if (to === undefined) break;
        const updated = await transitionRecord(tenantId, job!.visit.id, to);
        state = updated.lifecycle_state ?? to;
      }
      setRetryNonce((n) => n + 1);
    } catch (err) {
      setActionFailure(describeReadFailure(err));
    } finally {
      setActing(false);
    }
  }

  return (
    <div class="job">
      <FieldStatus />

      {/* No back link: wf03 draws none, the tab bar is the navigation here,
          and the top-left corner is both un-thumb-reachable and already
          occupied by the shell's account entry. */}
      <header class="job__head">
        <h1 class="job__title">{jobTitle(job)}</h1>
        <div class="job__sub">
          {[text(wo, 'wo_number') || wo?.display_name, address].filter(Boolean).join(' · ')}
          {job.urgent ? <span class="job__urgent"> · urgent</span> : null}
        </div>
      </header>

      <div class="job__body">
        {/* GETTING IN — first, always, before any action. */}
        <div class="job__access">
          <span class="job__access-k">Getting in</span>
          {access || (
            <span class="job__access-none">
              {site === null
                ? "We couldn't read this location, so there are no access notes to show."
                : 'No access notes for this location yet.'}
            </span>
          )}
        </div>

        {/* THE ONE PRIMARY ACTION, FULL WIDTH. */}
        {nextState !== undefined ? (
          <button type="button" class="job__primary" disabled={acting} onClick={goOnSite}>
            {acting ? 'Sending…' : 'On site'}
          </button>
        ) : (
          <div class="job__state">
            {visitState === 'on_site'
              ? "You're on site."
              : VISIT_CLOSED.has(visitState)
                ? `This visit is ${visitState.replace(/_/g, ' ')}.`
                : 'No next step on this visit.'}
          </div>
        )}

        {actionFailure !== null ? (
          // A write that failed is a REAL failure and says so loudly — which
          // is a different thing from the offline banner above, and styled
          // differently on purpose.
          <div class="job__action-failed" role="alert">
            {actionFailure.headline}
            <span class="job__say-soft">{actionFailure.detail}</span>
            <span class="job__say-soft">
              Nothing is queued on this phone yet, so this has to be sent while you have signal.
            </span>
          </div>
        ) : null}

        <button type="button" class="job__ghost" onClick={onCapture}>
          Photo · attach
        </button>

        {/* CV2 §4 — the job's own conversation, one tap from the job. The
            S5c walk found "a line that names something you cannot reach";
            this is the reach. Only when the work order itself was readable:
            a chat door on an unreadable job would be a door drawn on a
            wall. */}
        {wo !== null ? (
          <button
            type="button"
            class="job__ghost"
            data-testid="job-chat-door"
            onClick={() => onNavigate(routePath({ screen: 'conversation', lane: 'job', id: wo.id }))}
          >
            Job chat
          </button>
        ) : null}

        {/* CV3 — ON THIS JOB: the loop's endpoint, visible. A capture taken
            here and approved, or a note spoken here, shows up in this list
            — which is the whole promise "capture on a job -> see it on the
            job". Rendered only when the work order itself was readable (a
            list drawn against a job we could not read would be a guess),
            and it SAYS when nothing has landed rather than vanishing. */}
        {wo !== null ? (
          <div class="job__landed" data-testid="job-on-this-job">
            <span class="job__landed-k">On this job</span>
            {landed.length === 0 ? (
              <span class="job__landed-none">Nothing captured on this job yet.</span>
            ) : (
              landed.map(({ kind, row }) => {
                const when = new Date(row.created_at);
                const stamp = Number.isNaN(when.getTime())
                  ? ''
                  : `${shortDate(when)} · ${clockTime(when)}`;
                if (kind === 'note') {
                  // CV5 §5 — A NOTE THAT HAS A RECORDING SAYS SO AND REACHES
                  // IT. With one, the row becomes the door to the recording
                  // (the source viewer plays it, with this transcript beside
                  // it); without one it stays the plain line it always was.
                  const recording = recordingForNote.get(row.id);
                  if (recording === undefined) {
                    return (
                      <div class="job__landed-row" key={row.id}>
                        <div class="job__landed-main">
                          <span class="job__landed-body">{text(row, 'body') || row.display_name}</span>
                          <span class="job__landed-when">{stamp}</span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <button
                      type="button"
                      class="job__landed-row job__landed-row--source"
                      key={row.id}
                      data-testid="job-landed-voice-note"
                      onClick={() =>
                        onNavigate(
                          routePath({
                            screen: 'source',
                            id: recording.id,
                            from: routePath(id === undefined ? { screen: 'jobs' } : { screen: 'jobDetail', id }),
                          })
                        )
                      }
                    >
                      <SourceThumb
                        tenantId={tenantId}
                        sourceId={recording.id}
                        filename={text(recording, 'filename')}
                      />
                      <div class="job__landed-main">
                        <span class="job__landed-body">{text(row, 'body') || row.display_name}</span>
                        <span class="job__landed-when">{['Recording', stamp].filter(Boolean).join(' · ')}</span>
                      </div>
                    </button>
                  );
                }
                const label =
                  text(row, 'filename') || text(row, 'raw_text') || text(row, 'kind') || 'capture';
                const hasOriginal = text(row, 'media_ref') !== '';
                return (
                  <button
                    type="button"
                    class="job__landed-row job__landed-row--source"
                    key={row.id}
                    disabled={!hasOriginal && !text(row, 'raw_text')}
                    onClick={() =>
                      onNavigate(
                        routePath({
                          screen: 'source',
                          id: row.id,
                          from: routePath(id === undefined ? { screen: 'jobs' } : { screen: 'jobDetail', id }),
                        })
                      )
                    }
                  >
                    <SourceThumb tenantId={tenantId} sourceId={row.id} filename={text(row, 'filename')} />
                    <div class="job__landed-main">
                      <span class="job__landed-body">{label}</span>
                      <span class="job__landed-when">
                        {[text(row, 'kind'), stamp].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        ) : null}

        <div class="job__kv">
          <span>Issue</span>
          <b>{job.issue || (wo === null ? "Couldn't read this job" : 'None recorded')}</b>
        </div>
        <div class="job__kv">
          <span>Equipment</span>
          <b>
            {equipmentNames.length > 0 ? equipmentNames.join(' · ') : 'None recorded'}
            {equipmentUnread > 0 ? ` · ${equipmentUnread} unreadable` : ''}
          </b>
        </div>
        <div class="job__kv">
          <span>Last visit</span>
          <b>
            {lastVisit === null
              ? 'None here yet'
              : `${shortDate(lastVisit.on)}${lastVisit.mine ? ' · you' : ''}`}
          </b>
        </div>
        <div class="job__kv">
          <span>Flat rate</span>
          <b>
            {!priority
              ? 'None named'
              : rate === null
                ? `${priority} — not in the rate book`
                : `${text(rate, 'rate_type')}${rateHours === null ? '' : ` · ${rateHours} h book`}`}
          </b>
        </div>
        {text(wo, 'supplemental_issues') ? (
          <p class="job__more">{text(wo, 'supplemental_issues')}</p>
        ) : null}
        <div class="job__kv">
          <span>Booked</span>
          <b>{clockTime(job.startsAt) || 'No time set'}</b>
        </div>
      </div>
    </div>
  );
}

/** WHICH VISIT THIS JOB MEANS. A work order can carry several of the tech's
 *  own visits — a return trip, a callback, a job that ran over two days —
 *  and the one the screen is about is the LIVE one, never whichever the
 *  sweep happened to sort first. (Found by Job.test.tsx before a browser
 *  could: a job with a June visit in its history opened on the June visit.)
 *
 *  In order: one the tech has already declared themselves on · one booked
 *  today and still open · any still-open one · otherwise the most recent,
 *  so a finished job still opens on the visit that finished it. */
function visitOn(jobs: FieldJob[], woId: string, now: Date): FieldJob | null {
  const here = jobs.filter((j) => j.workOrder?.id === woId);
  if (here.length === 0) return null;
  const open = here.filter((j) => !VISIT_CLOSED.has(j.visit.lifecycle_state ?? ''));
  return (
    here.find((j) => j.visit.lifecycle_state === 'on_site') ??
    here.find((j) => j.visit.lifecycle_state === 'en_route') ??
    open.find((j) => j.startsAt !== null && isSameDay(j.startsAt, now)) ??
    open[0] ??
    here[here.length - 1] ??
    null
  );
}

/** The most recent OTHER visit at this job's location, and whether it was
 *  mine. Built from the same sweeps the rest of the screen rides: a visit's
 *  site is its work order's site, and every readable work order came back
 *  with its `wo_site` link. `mine` is a FACT — the visit's `tech_profile`
 *  link against GET /api/me/record — never an inference from row scope. */
function latestOtherVisitHere(
  jobs: FieldJob[],
  self: FieldJob,
  read: FieldRead | null,
  myRecordId: string | null
): LastVisit | null {
  const siteId = self.site?.id;
  if (siteId === undefined || read === null) return null;
  let best: LastVisit | null = null;
  for (const j of jobs) {
    if (j.visit.id === self.visit.id) continue;
    if (j.site?.id !== siteId) continue;
    if (j.startsAt === null) continue;
    if (best !== null && j.startsAt.getTime() <= best.on.getTime()) continue;
    const links = read.visitLinks[j.visit.id];
    const techId = links === undefined ? null : linkedId(links, j.visit.id, 'tech_profile');
    best = { on: j.startsAt, mine: myRecordId !== null && techId === myRecordId };
  }
  return best;
}
